// src/context/AuthContext.js
import { createContext, useContext, useEffect, useState } from 'react';
import {
  onAuthStateChanged,
  signInWithPopup,
  signOut,
  multiFactor,
  getMultiFactorResolver,
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { getSectionForSlug } from '../config/modules';
import { auth, db, googleProvider } from '../config/firebase';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mfaResolver, setMfaResolver] = useState(null);

  // Load user profile and role from Firestore
  async function loadUserProfile(firebaseUser) {
    if (!firebaseUser) {
      setUserProfile(null);
      return;
    }

    const userRef = doc(db, 'users', firebaseUser.uid);
    const snap = await getDoc(userRef);

    if (snap.exists()) {
      // Update last login
      await setDoc(userRef, { lastLogin: serverTimestamp() }, { merge: true });
      setUserProfile({ uid: firebaseUser.uid, ...snap.data() });
    } else {
      // First login — create profile, default to 'user' role
      // Admin must elevate permissions after first login
      const newProfile = {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName,
        photoURL: firebaseUser.photoURL,
        role: 'user',
        workspaceGroups: [],
        mfaEnrolled: false,
        active: true,
        createdAt: serverTimestamp(),
        lastLogin: serverTimestamp(),
      };
      await setDoc(userRef, newProfile);
      setUserProfile(newProfile);
    }
  }

  // Sign in with Google Workspace SSO
  async function signInWithGoogle() {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      return { success: true, user: result.user };
    } catch (error) {
      // MFA required
      if (error.code === 'auth/multi-factor-auth-required') {
        const resolver = getMultiFactorResolver(auth, error);
        setMfaResolver(resolver);
        return { success: false, requiresMfa: true };
      }
      return { success: false, error: error.message };
    }
  }

  async function signOutUser() {
    await signOut(auth);
    setUser(null);
    setUserProfile(null);
  }

  // Check if user has a specific role
  function hasRole(...roles) {
    if (!userProfile) return false;
    return roles.includes(userProfile.role);
  }

  // Check if user has a specific permission
  function hasPermission(permission) {
    if (!userProfile) return false;
    if (userProfile.role === 'admin') return true; // admins have all permissions
    return userProfile.permissions?.includes(permission) ?? false;
  }

  // Check if user has access to a module (by page slug or section id)
  function hasModuleAccess(slugOrSectionId) {
    if (!userProfile) return false;
    if (userProfile.role === 'admin') return true;
    const moduleAccess = userProfile.moduleAccess;
    if (!moduleAccess || moduleAccess.length === 0) return true; // no restrictions set
    // Map page slug → section id
    const section = getSectionForSlug(slugOrSectionId);
    const sectionId = section ? section.id : slugOrSectionId;
    return moduleAccess.includes(sectionId);
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      await loadUserProfile(firebaseUser);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const value = {
    user,
    userProfile,
    loading,
    mfaResolver,
    signInWithGoogle,
    signOutUser,
    hasRole,
    hasPermission,
    hasModuleAccess,
    isAdmin: userProfile?.role === 'admin',
    isManager: userProfile?.role === 'manager' || userProfile?.role === 'admin',
    isQA: userProfile?.role === 'qa' || userProfile?.role === 'admin',
    isAuthenticated: !!user && !!userProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
